import { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { J3Select } from "@/components/common/ProjectJ3";
import { MapPin, FileText, Pencil, Trash2, Download, Link as LinkIcon } from "lucide-react";
import LotPickerL2 from "@/components/crm/LotPickerL2";
import OptionalLotSection from "@/components/documents/OptionalLotSection";
import DeskConfirm from "@/components/crm/DeskConfirm";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import useUploadQueue from "@/hooks/useUploadQueue";
import { FieldInput } from "@/components/documents/DocumentCreateDialog";
import { useRequiredFields, requiredStyle } from "@/lib/useRequiredFields";
import { MissingSummary } from "@/components/common/RequiredField";

// Local mini section separator (identical style to the one in
// DocumentCreateDialog — kept private to avoid an extra export just for
// this dialog).
const SEPARATOR_BLUE_SOFT = "#4A8CD8";
function SectionSeparator({ label }) {
  return (
    <div className="flex items-center gap-2 mb-1">
      <div
        className="text-[11px] font-semibold uppercase tracking-[0.14em] shrink-0"
        style={{ color: SEPARATOR_BLUE_SOFT }}
      >
        {label}
      </div>
      <div
        className="h-px flex-1"
        style={{ background: "linear-gradient(90deg, rgba(74,140,216,0.3), transparent)" }}
      />
    </div>
  );
}

/**
 * DocumentEditDialog — edit an existing document's dynamic values.
 *
 * Reuses the exact same `FieldInput` renderer as the creation dialog so
 * height/spacing rules stay consistent (see the 40 px canonical rule). File
 * fields already populated show their current filename; a new upload
 * replaces it silently on save.
 *
 * Server contract:
 *   PATCH /documents/{id}         → body { values, project_id }
 *   POST  /documents/{id}/upload-field?field_key=... → new file, returns doc
 */
export default function DocumentEditDialog({ open, onOpenChange, doc, docType, projects, onSaved }) {
  const [values, setValues] = useState({});
  const [projectId, setProjectId] = useState("");
  const [saving, setSaving] = useState(false);
  const [fileDel, setFileDel] = useState(null); // iter387 — remplace window.confirm
  // iter379 pt 5 — « Lot lié » (types à lien de lot) : facultatif, jamais automatique ; suggestion depuis le champ N° LOT.
  const lotLinked = !!docType?.lot_linked;
  const hasLotField = lotLinked || (docType?.fields || []).some((f) => f.kind === "lot" || /\blot\b/i.test(`${f.label || ""} ${f.key || ""}`)); // iter417 pt 3
  const [lotId, setLotId] = useState("");
  const [lotIds, setLotIds] = useState([]); // iter419 pt 2 — lots liés facultatifs (plusieurs)
  const [lotOpen, setLotOpen] = useState(false); // iter382 pt 4 — liste ouverte → fenêtre élargie à 860 px
  const [projectLots, setProjectLots] = useState([]);

  const fileQueue = useUploadQueue();

  // Reset form each time the dialog opens with a fresh doc.
  useEffect(() => {
    if (!open || !doc) return;
    setValues({ ...(doc.values || {}) });
    setProjectId(doc.project_id || "");
    setLotId((doc.crm_lot_ids || [])[0] || "");
    setLotIds([...(doc.crm_lot_ids || [])]);
    fileQueue.reset();
  }, [open, doc?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open && projectId) api.get(`/projects/${projectId}/lots`).then(({ data: d }) => setProjectLots(d.items || [])).catch(() => setProjectLots([])); else setProjectLots([]); }, [open, projectId]);
  const lotNoField = (docType?.fields || []).find((f) => /n[°o]?\s*lot|num[ée]ro.*lot|lot.*num/i.test(f.label || "") || /lot/i.test(f.key || ""));
  const lotNoRaw = String(values[lotNoField?.key] ?? "").trim();

  const entriesByField = useMemo(() => {
    const map = {};
    for (const e of fileQueue.entries) {
      const k = e.meta?.fieldKey || "__none__";
      (map[k] = map[k] || []).push(e);
    }
    return map;
  }, [fileQueue.entries]);

  const missing = useMemo(() => {
    if (!docType) return [];
    return (docType.fields || []).filter((f) => {
      if (!f.required) return false;
      const v = values[f.key];
      if (f.type === "file") {
        // A file field can hold either a single legacy object {storage_path,…}
        // OR a list of such objects (rev10, 2026-02). Both count as "saved".
        const hasSaved = Array.isArray(v)
          ? v.some((x) => x && typeof x === "object" && x.storage_path)
          : !!(v && typeof v === "object" && v.storage_path);
        const hasStaged = (entriesByField[f.key] || []).some((e) => e.status !== "error");
        return !hasSaved && !hasStaged;
      }
      return v === undefined || v === null || v === "" || v === false;
    }).map((f) => ({ key: f.key, label: f.label }));
  }, [docType, values, entriesByField]);
  const locationRequired = docType?.location_required ?? true;
  const req = useRequiredFields([
    { key: "project", label: "Siège / Projet", ok: !docType || !locationRequired || !!projectId },
    ...((docType?.fields || []).filter((f) => f.required).map((f) => ({ key: `field-${f.key}`, label: f.label, ok: !missing.some((m) => m.key === f.key) }))),
  ]);

  if (!doc || !docType) return null;

  // Split "Titre" OUT of the Details grid — rendered up top on its own
  // line (spec 2026-02-11, §7bis). Backend still keeps it in
  // docType.fields; the UI just hoists it out of the grid.
  const titreField = (docType.fields || []).find((f) => (f.key || "").toLowerCase() === "titre") || null;
  const detailsFields = (docType.fields || []).filter((f) => (f.key || "").toLowerCase() !== "titre");

  const canSubmit =
    !saving &&
    (!locationRequired || !!projectId) &&
    missing.length === 0;

  const submit = async () => {
    if (saving || !req.validate()) return;
    setSaving(true);
    try {
      // 1. Patch simple values (everything except pending file uploads).
      const patchValues = {};
      for (const f of docType.fields || []) {
        if (f.type === "file") continue;   // handled below
        if (Object.prototype.hasOwnProperty.call(values, f.key)) {
          patchValues[f.key] = values[f.key];
        }
      }
      await api.patch(`/documents/${doc.id}`, {
        values: patchValues,
        project_id: projectId || null,
        crm_lot_ids: hasLotField ? (lotId ? [lotId] : []) : lotIds, // iter410 L6 / iter419 pt 2 — lot lié facultatif pour tous les types
      });

      // 2. Upload any newly staged file fields.
      const pendingUploads = [];
      for (const entry of fileQueue.entries) {
        if (entry.status !== "success" && entry.status !== "error") {
          pendingUploads.push(entry);
        }
      }
      // For simplicity we just POST-upload every staged file synchronously.
      for (const [k, arr] of Object.entries(entriesByField)) {
        for (const entry of arr) {
          if (entry.status === "success") continue;
          const form = new FormData();
          form.append("file", entry.file);
          await api.post(
            `/documents/${doc.id}/upload-field?field_key=${encodeURIComponent(k)}`,
            form,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
        }
      }

      toast.success(`Document ${doc.reference} mis à jour`);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error("Échec de la mise à jour", { description: err.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  const doFileDel = async () => {
    const { file, key, idx } = fileDel;
    try {
      const { data: fresh } = await api.delete(`/documents/${doc.id}/field-file/${encodeURIComponent(key)}?idx=${idx}`);
      setValues((prev) => ({ ...prev, [key]: fresh?.values?.[key] ?? null }));
      toast.success("Fichier supprimé", { description: file.original_filename });
    } catch (err) {
      toast.error("Suppression impossible", { description: err.response?.data?.detail || "" });
    } finally { setFileDel(null); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DeskConfirm open={!!fileDel} title={`Supprimer ${fileDel?.file?.original_filename || "ce fichier"} ?`} message="Le fichier sera retiré du champ." confirmLabel="Supprimer" onClose={() => setFileDel(null)} onConfirm={doFileDel} testId="doc-edit-file-delete" />
      <DialogContent
        className={`${lotOpen ? "max-w-[860px]" : "max-w-[560px]"} p-0 gap-0 border-0 overflow-hidden`}
        style={{
          transition: "max-width .2s ease",
          background: "linear-gradient(180deg, #232323, #1B1B1B)",
          border: "1px solid #2E2E2E",
          borderRadius: 16,
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        }}
        data-testid="doc-edit-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #3568B0, #1A3358)",
                color: "#fff",
              }}
            >
              <Pencil size={18} />
            </div>
            <div>
              <DialogTitle className="text-base leading-tight">
                Modifier le document
              </DialogTitle>
              <div className="text-xs text-muted-foreground mt-0.5">
                <span className="font-mono text-[#5A9EE0]">{doc.reference}</span>
                {" · "}
                {docType.name}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="px-5 pb-3 space-y-3 max-h-[70vh] overflow-y-auto scrollbar-thin">
          {/* -------- Type de document (readonly) + Titre (editable) ----- */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground block mb-1.5">
                Type de document
              </label>
              <div
                className="h-10 min-h-10 flex items-center px-3 rounded-[9px] text-sm text-muted-foreground select-none"
                style={{ background: "#1A1A1A", border: "1.5px solid rgba(255,255,255,0.06)", opacity: 0.85 }}
                title="Le type est fixé à la création — non modifiable"
                data-testid="doc-edit-type-readonly"
              >
                <span className="truncate">{docType.name}</span>
              </div>
            </div>
            <div data-field={`field-${titreField?.key || "titre"}`}>
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground block mb-1.5">
                Titre {titreField?.required !== false && <span className="text-red-500">*</span>}
              </label>
              {/* Spec rev126 (2026-02-05) — mise en MAJUSCULES automatique
                  du Titre (feedback visuel + normalisation stockage). */}
              <Input
                type="text"
                value={values[titreField?.key || "titre"] ?? ""}
                onChange={(e) => setValues((prev) => ({ ...prev, [titreField?.key || "titre"]: (e.target.value || "").toLocaleUpperCase("fr-FR") }))}
                placeholder="Saisir titre…"
                className="h-10 min-h-10 focus-visible:ring-0 focus-visible:border-[#2B579A]"
                style={{ background: "#1A1A1A", border: "1.5px solid rgba(255,255,255,0.08)", borderRadius: 9, textTransform: "uppercase", ...requiredStyle(req.has(`field-${titreField?.key || "titre"}`)) }}
                data-testid="doc-edit-field-titre"
                data-no-capitalize
              />
            </div>
          </div>

          <SectionSeparator label={`Détails · ${detailsFields.length + 1} champ${detailsFields.length + 1 > 1 ? "s" : ""}`} />

          <div className="grid grid-cols-2 gap-3">
            {/* Siège / Projet — premier champ de la section Détails */}
            <div data-field="project">
              <label className="text-[11px] uppercase tracking-widest text-muted-foreground block mb-1.5 flex items-center gap-1">
                <MapPin size={11} /> Siège / Projet
                {locationRequired && <span className="text-red-500">*</span>}
                {!locationRequired && (
                  <span className="text-[10px] normal-case tracking-normal text-muted-foreground">(optionnelle)</span>
                )}
              </label>
              <J3Select projects={projects || []} value={projectId} onChange={setProjectId} testId="doc-edit-project" triggerStyle={{ height: 40, background: "#1A1A1A", border: "1.5px solid rgba(255,255,255,0.08)", borderRadius: 9, ...requiredStyle(req.has("project")) }} />
            </div>

            {lotLinked && (
              <div data-field="crm_lot" style={{ gridColumn: "1 / -1" }}>
                <label className="text-[11px] uppercase tracking-widest text-muted-foreground block mb-1.5 flex items-center gap-1">
                  <LinkIcon size={11} /> Lot lié <span className="text-[10px] normal-case tracking-normal text-muted-foreground">(facultatif)</span>
                </label>
                <LotPickerL2 lots={projectLots} value={lotId} onChange={setLotId} suggestNo={lotNoRaw} disabled={!projectId} placeholder={projectId ? "Lot, client, type…" : "Choisir d'abord le projet"} onOpenChange={setLotOpen} testId="doc-edit-lot" />
              </div>
            )}

            {detailsFields.map((f, idx) => {
              const totalFields = 1 + detailsFields.length;
              const isLast = idx === detailsFields.length - 1;
              const spanFull = isLast && (totalFields % 2 === 1);

              // For file fields, if a value is already saved, we still allow
              // the user to attach a new one — the queue drives the upload.
              return (
                <FieldInput
                  key={f.id || f.key}
                  field={f}
                  error={req.has(`field-${f.key}`)}
                  spanFull={spanFull}
                  value={values[f.key]}
                  onChange={(v) => setValues((prev) => ({ ...prev, [f.key]: v }))}
                  documentTypeId={docType.id}
                  fileEntries={entriesByField[f.key] || []}
                  onFile={(file) => fileQueue.enqueue(file, { fieldKey: f.key })}
                  onFileCancel={(id) => fileQueue.cancel(id)}
                  onFileRemove={(id) => fileQueue.remove(id)}
                />
              );
            })}
          </div>

          {/*
            Existing saved-file summary — spec 2026-02-XX rev10 : liste
            complète des fichiers déjà attachés au champ, avec bouton
            « Supprimer » individuel par fichier + « Télécharger ». Les
            nouveaux uploads (via FieldInput ci-dessus) sont APPENDÉS en
            plus des existants côté backend.
          */}
          {(docType.fields || [])
            .filter((f) => f.type === "file")
            .map((f) => {
              const raw = values[f.key];
              // Normalise : dict legacy OU list → toujours iterable de {storage_path, original_filename, ...}
              const files = Array.isArray(raw)
                ? raw.filter((x) => x && typeof x === "object" && x.storage_path)
                : (raw && typeof raw === "object" && raw.storage_path ? [raw] : []);
              if (files.length === 0) return null;
              return (
                <div
                  key={`saved-${f.key}`}
                  className="text-[11px] space-y-1"
                  data-testid={`doc-edit-savedfiles-${f.key}`}
                >
                  <div className="text-muted-foreground uppercase tracking-widest text-[10px]">
                    Fichiers actuels · <span className="normal-case text-foreground font-medium">{f.label}</span>
                  </div>
                  {files.map((file, idx) => (
                    <div
                      key={`${file.storage_path}-${idx}`}
                      className="flex items-center gap-2 pl-2"
                    >
                      <FileText size={12} className="text-[#5A9EE0] shrink-0" />
                      <span className="italic truncate flex-1">{file.original_filename || "fichier"}</span>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const resp = await api.get(
                              `/documents/${doc.id}/field-file/${encodeURIComponent(f.key)}?idx=${idx}`,
                              { responseType: "blob" },
                            );
                            const url = URL.createObjectURL(resp.data);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = file.original_filename || "fichier";
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            URL.revokeObjectURL(url);
                          } catch {
                            toast.error("Téléchargement impossible");
                          }
                        }}
                        className="text-[10px] underline text-[#5A9EE0] hover:text-white flex items-center gap-0.5"
                        title="Télécharger"
                        data-testid={`doc-edit-savedfile-download-${f.key}-${idx}`}
                      >
                        <Download size={11} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setFileDel({ file, key: f.key, idx })}
                        className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-0.5"
                        title="Supprimer ce fichier"
                        data-testid={`doc-edit-savedfile-delete-${f.key}-${idx}`}
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              );
            })}

          {!hasLotField && <OptionalLotSection lots={projectLots} values={lotIds} onChange={setLotIds} projectId={projectId} projectName={(projects || []).find((p) => p.id === projectId)?.name || ""} suggestNo={lotNoRaw} onOpenChange={setLotOpen} testId="doc-edit-optional-lot" />}

          <MissingSummary errors={req.errors} testId="doc-edit-missing" />
        </div>

        <DialogFooter className="px-5 pb-5 pt-1 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="doc-edit-cancel">
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-incomplete={!canSubmit || undefined}
            className="text-white border-0 disabled:opacity-50"
            style={{ background: "#0078D4", boxShadow: "0 4px 14px rgba(0,120,212,0.35)" }}
            data-testid="doc-edit-save"
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
